import { Card, Chip, ListItemIcon, ListItemText, MenuItem, Typography } from "@mui/material";
import { Stack } from "@mui/system";
import moment from "moment";
import { FC, useContext, useRef } from "react";
import { DbOrderManualTaskPlanningResourceInfos } from "../../../Core/Api/Types/db-order-manual-task-planning-resource-infos";
import { GroupPlanningViewInfos } from "../../../Core/Api/Types/group-planning-view-infos";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import MoreMenu, { MoreMenuHandle } from "../../Misc/MoreMenu";
import ModeEditOutlinedIcon from "@mui/icons-material/ModeEditOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import ModalCreateOrUpdateResourceView, {
  ModalCreateOrUpdateResourceViewElementProps,
} from "../../Modals/ModalCreateOrUpdateResourceView/mocal-create-or-update-resource-view";
import { ModalContext } from "../../Modals";
import ModalConfirmAction, {
  ModalConfirmActionElementProps,
} from "../../Modals/ModalConfirmAction/modal-confirm-action";
import { useTranslation } from "react-i18next";

export interface ResourceViewCardProps {
  viewInfos: GroupPlanningViewInfos;
  resources: DbOrderManualTaskPlanningResourceInfos[];
  editResourceView: (resourceView: GroupPlanningViewInfos) => void;
  deleteResourceView: (resourceView: GroupPlanningViewInfos) => void;
}

const ResourceViewCard: FC<ResourceViewCardProps> = (props) => {
  const { t } = useTranslation();
  const { viewInfos, resources, editResourceView, deleteResourceView } = props;

  const modalContext = useContext(ModalContext);
  const { openModal } = { ...modalContext };

  const moreMenuRef = useRef<MoreMenuHandle>(null);

  const handleEditResourceView = () => {
    if (!openModal) return;
    openModal<ModalCreateOrUpdateResourceViewElementProps>(ModalCreateOrUpdateResourceView, {
      mode: "update",
      availableResources: resources,
      resourceViewToUpdate: viewInfos,
      addResourceViewToGroup: (_) => false,
      editResourceViewOfGroup: editResourceView,
    });
    moreMenuRef.current?.closeMenu();
  };

  const handleDeleteResourceView = () => {
    if (!openModal) return;
    openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
      onConfirm: (_) => deleteResourceView(viewInfos),
      title: t("modal:title.confirmViewDeletion"),
    });
  };

  return (
    <Card variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h5">{viewInfos.name}</Typography>
          <MoreMenu ref={moreMenuRef} direction="vertical" size="small" sx={{ color: "text.secondary" }}>
            <MenuItem onClick={handleEditResourceView}>
              <ListItemIcon>
                <ModeEditOutlinedIcon />
              </ListItemIcon>
              <ListItemText>{t("common:editUpperCase")}</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleDeleteResourceView}>
              <ListItemIcon>
                <DeleteOutlinedIcon color="error" />
              </ListItemIcon>
              <ListItemText>{t("common:delete")}</ListItemText>
            </MenuItem>
          </MoreMenu>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Typography variant="body2" fontWeight={500} color="text.tier">
            {t("card:resourceViewCard.body.createdOrUpdatedAt")} :
          </Typography>
          <Typography variant="body2">{moment(viewInfos.date).format(GlobalConst.MOMENT_DATE_FORMAT_FULL)}</Typography>
        </Stack>
        {resources
          .filter((r) => viewInfos.resourcesIds.includes(r.id))
          .map((resource, index) => (
            <Chip key={index} label={resource.name} />
          ))}
      </Stack>
    </Card>
  );
};

export default ResourceViewCard;
